import L from 'leaflet';
import { feature } from 'topojson-client';
import { presimplify, simplify } from 'topojson-simplify';

// Lighter area outlines for zoomed-out views. Leaflet re-projects every point of all ~3,360 areas on each
// zoom step; when zoomed out most of those points lie within a pixel of their neighbours. Each level is
// simplified to half a square pixel at its highest zoom, so it looks the same as the full outline there.
// From FULL_FROM_ZOOM up the full outlines are drawn (the data is already at pixel precision there).
// Shared by kartvy (Map.js) and listvy (ListViewPage.js). Only what is drawn changes: point-in-polygon,
// bounds and scoring keep using the full geometry.

const FULL_FROM_ZOOM = 9;
// One square screen pixel in planar lng/lat degrees² at zoom z, at Sweden's latitude.
const squarePixel = (zoom) => {
    const lng = 360 / (256 * 2 ** zoom);
    return lng * lng * Math.cos((59 * Math.PI) / 180);
};
const LEVELS = [6, 8].map((maxZoom) => ({ maxZoom, minWeight: 0.5 * squarePixel(maxZoom) }));

const areaCode = (featureEntry) => String(featureEntry?.properties?.regsokod || '').trim();

const levelForZoom = (zoom) => {
    if (zoom >= FULL_FROM_ZOOM) return 'full';
    const level = LEVELS.find((entry) => zoom <= entry.maxZoom);
    return level ? String(level.maxZoom) : 'full';
};

const toLatLngs = (geometry) => L.GeoJSON.coordsToLatLngs(geometry.coordinates, geometry.type === 'Polygon' ? 1 : 2);

/**
 * Switches the drawn outline of each area in a GeoJSON layer to the level for the map's zoom.
 * setTopology(topology) builds the light levels (call when idle); apply(layer, zoom, redraw) swaps the
 * outlines: before a zoom animation pass redraw=false (Leaflet re-projects at zoomend anyway), otherwise
 * redraw=true. Until the levels are built, everything stays on the full outline.
 */
export const createAreaGeometrySwitch = () => {
    // level -> Map(areaCode -> geometry)
    let geometryByLevel = null;

    const setTopology = (topology) => {
        const objectKey = Object.keys(topology?.objects || {})[0];
        if (!objectKey) return;
        const prepared = presimplify(topology);
        geometryByLevel = new Map(LEVELS.map(({ maxZoom, minWeight }) => {
            const simplified = simplify(prepared, minWeight);
            const byCode = new Map();
            feature(simplified, simplified.objects[objectKey]).features.forEach((entry) => {
                const code = areaCode(entry);
                if (code && entry.geometry) byCode.set(code, entry.geometry);
            });
            return [String(maxZoom), byCode];
        }));
    };

    const apply = (geoJsonLayer, zoom, redraw = true) => {
        if (!geoJsonLayer || !geometryByLevel || !Number.isFinite(zoom)) return;
        const level = levelForZoom(zoom);
        geoJsonLayer.eachLayer((layer) => {
            const current = layer.__areaGeometryLevel || 'full';
            if (current === level || typeof layer.getLatLngs !== 'function') return;
            const cache = layer.__areaLatLngs || (layer.__areaLatLngs = {});
            if (!cache.full) cache.full = layer.getLatLngs();
            if (!cache[level]) {
                const geometry = geometryByLevel.get(level)?.get(areaCode(layer.feature));
                if (!geometry) return;
                cache[level] = toLatLngs(geometry);
            }
            // Private Leaflet API (stable in 1.9): set the outline without an immediate redraw.
            if (redraw || typeof layer._setLatLngs !== 'function') layer.setLatLngs(cache[level]);
            else layer._setLatLngs(cache[level]);
            layer.__areaGeometryLevel = level;
        });
    };

    return { setTopology, apply, isReady: () => Boolean(geometryByLevel) };
};
